// LUMINA · pages/vajra/MyJourney.jsx
// Phase 68: 弟子修行关系链统一视图 — 上师 · 法教 · 修行路径

import React, { useState, useEffect } from 'react';
import { API, GD, GDL, GDB, BG, TXT, TXT2 } from './constants';

function useVajraToken() {
  return localStorage.getItem("vajra_token") || sessionStorage.getItem("vajra_token");
}

function EmptyState({ icon, title, hint, linkLabel, linkHref }) {
  return (
    <div style={{ textAlign: "center", padding: "32px 16px", color: TXT2 }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontWeight: 600, color: TXT, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>{hint}</div>
      {linkHref && (
        <a href={linkHref} style={{ color: GD, fontSize: 13, textDecoration: "none" }}>
          {linkLabel} →
        </a>
      )}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{
      background: "#fff",
      border: `1px solid rgba(212,175,55,0.15)`,
      borderRadius: 12,
      padding: "20px 18px",
      minHeight: 280,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}>
      <div style={{
        fontFamily: "'Noto Serif SC', serif",
        fontSize: 15, fontWeight: 700, color: GD,
        borderBottom: `1px solid rgba(212,175,55,0.15)`,
        paddingBottom: 10, marginBottom: 16,
        letterSpacing: ".04em",
      }}>{title}</div>
      {children}
    </div>
  );
}

function TeachersColumn({ teachers }) {
  if (!teachers.length) return (
    <EmptyState
      icon="🪷"
      title="尚无传承上师记录"
      hint="参加法会、获取灌顶后，这里将展示您的上师"
      linkLabel="浏览近期法会"
      linkHref="/vajra/calendar"
    />
  );

  const shown = teachers.slice(0, 5);
  return (
    <>
      {shown.map(t => (
        <div key={t.id} style={{
          display: "flex", alignItems: "center", gap: 10,
          marginBottom: 14, paddingBottom: 12,
          borderBottom: `1px solid ${GDB}22`,
        }}>
          {t.photo_url
            ? <img src={t.photo_url} alt={t.name_zh}
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
            : <div style={{
                width: 40, height: 40, borderRadius: "50%", background: `${GDL}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, flexShrink: 0,
              }}>☸</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: TXT, fontSize: 13, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {t.name_zh}
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {t.tradition && (
                <span style={{ fontSize: 11, background: `${GDL}15`, color: GD, borderRadius: 99, padding: "1px 7px" }}>
                  {t.tradition}
                </span>
              )}
              {t.lineage && (
                <span style={{ fontSize: 11, background: "rgba(0,0,0,0.04)", color: TXT2, borderRadius: 99, padding: "1px 7px" }}>
                  {t.lineage}
                </span>
              )}
            </div>
          </div>
          {t.credential_count > 0 && (
            <div style={{
              fontSize: 11, background: `${GDL}20`, color: GD,
              borderRadius: 99, padding: "2px 8px", flexShrink: 0,
            }}>
              {t.credential_count} 项资质
            </div>
          )}
        </div>
      ))}
      {teachers.length > 5 && (
        <a href="/vajra/my-path" style={{ fontSize: 12, color: GD, textDecoration: "none" }}>
          + {teachers.length - 5} 位上师 →
        </a>
      )}
    </>
  );
}

function TeachingsColumn({ teachings }) {
  if (!teachings.length) return (
    <EmptyState
      icon="📚"
      title="尚无法教记录"
      hint="获取灌顶或完成法教后，这里将显示您接受的法教"
      linkLabel="浏览教法图书馆"
      linkHref="/vajra"
    />
  );

  const shown = teachings.slice(0, 5);
  return (
    <>
      {shown.map(t => (
        <div key={t.id} style={{
          marginBottom: 12, paddingBottom: 12,
          borderBottom: `1px solid ${GDB}22`,
        }}>
          <div style={{ fontWeight: 600, color: TXT, fontSize: 13, marginBottom: 3, lineHeight: 1.4 }}>
            {t.title_zh}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: TXT2 }}>
              {t.teacher_name || ""}
            </span>
            {t.received_at && (
              <span style={{ fontSize: 11, color: TXT2 }}>{t.received_at}</span>
            )}
          </div>
        </div>
      ))}
      {teachings.length > 5 && (
        <a href="/vajra" style={{ fontSize: 12, color: GD, textDecoration: "none" }}>
          + 全部 {teachings.length} 项法教 →
        </a>
      )}
    </>
  );
}

function PathColumn({ pathNodes }) {
  if (!pathNodes.length) return (
    <EmptyState
      icon="🧘"
      title="修行次第尚未开始"
      hint="开始修行次第后，这里将显示您的修行进度"
      linkLabel="查看修行次第"
      linkHref="/vajra/my-path"
    />
  );

  // group by tradition
  const groups = {};
  pathNodes.forEach(n => {
    if (!groups[n.tradition]) groups[n.tradition] = [];
    groups[n.tradition].push(n);
  });

  const total = pathNodes.length;
  const done = pathNodes.filter(n => n.status === "completed").length;

  function statusIcon(s) {
    if (s === "completed") return "✅";
    return "⏳";
  }

  const shownNodes = pathNodes.slice(0, 8);
  const byTraditionShown = {};
  shownNodes.forEach(n => {
    if (!byTraditionShown[n.tradition]) byTraditionShown[n.tradition] = [];
    byTraditionShown[n.tradition].push(n);
  });

  return (
    <>
      {/* Progress bar */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: TXT2, marginBottom: 4 }}>
          <span>总进度</span>
          <span>{done}/{total} ({Math.round(done/total*100)}%)</span>
        </div>
        <div style={{ background: `${GDB}30`, borderRadius: 99, height: 6 }}>
          <div style={{
            background: GD, borderRadius: 99, height: 6,
            width: `${total ? Math.round(done/total*100) : 0}%`,
            transition: "width .4s ease",
          }} />
        </div>
      </div>

      {Object.entries(byTraditionShown).map(([trad, nodes]) => (
        <div key={trad} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: GD, fontWeight: 600, marginBottom: 6, letterSpacing: ".05em" }}>
            {trad.replace(/_/g, " ").toUpperCase()}
          </div>
          {nodes.map(n => (
            <div key={n.id} style={{
              display: "flex", alignItems: "center", gap: 8,
              marginBottom: 6, fontSize: 12, color: TXT,
            }}>
              <span style={{ flexShrink: 0 }}>{statusIcon(n.status)}</span>
              <span style={{ flex: 1, lineHeight: 1.4 }}>{n.name_zh}</span>
              {n.completed_at && (
                <span style={{ fontSize: 11, color: TXT2, flexShrink: 0 }}>{n.completed_at}</span>
              )}
            </div>
          ))}
        </div>
      ))}

      <a href="/vajra/my-path" style={{ fontSize: 12, color: GD, textDecoration: "none" }}>
        查看完整次第树 →
      </a>
    </>
  );
}

export default function MyJourney() {
  const token = useVajraToken();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/vajra/me/journey`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [token]);

  if (!token) return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: TXT2 }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>🔒</div>
        <div>请先<a href="/vajra" style={{ color: GD }}>登录</a>查看修行记录</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: TXT2 }}>
      载入中…
    </div>
  );

  if (error) return (
    <div style={{ background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#c00" }}>
      加载失败（{error}）
    </div>
  );

  const teachers = data?.teachers || [];
  const teachings = data?.teachings || [];
  const pathNodes = data?.path_nodes || [];
  const summary = data?.summary || {};

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "28px 16px 48px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <a href="/vajra" style={{ fontSize: 12, color: GD, textDecoration: "none" }}>如意宝金刚社群</a>
          <h1 style={{
            fontFamily: "'Noto Serif SC', serif", fontSize: 22, fontWeight: 700,
            color: GD, margin: "10px 0 6px", letterSpacing: ".04em",
          }}>
            🧘 我的修行
          </h1>
          <div style={{ fontSize: 13, color: TXT2 }}>
            {summary.total_teachers > 0 && `${summary.total_teachers} 位传承上师 · `}
            {summary.total_teachings > 0 && `${summary.total_teachings} 项法教 · `}
            {summary.path_completion_pct > 0 && `次第完成 ${summary.path_completion_pct}%`}
            {!summary.total_teachers && !summary.total_teachings && !summary.path_completion_pct && "暂无记录，参加法会后将自动更新"}
          </div>
        </div>

        {/* Three-column grid */}
        <div className="mj-grid" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 16,
        }}>
          <SectionCard title="我的上师">
            <TeachersColumn teachers={teachers} />
          </SectionCard>
          <SectionCard title="我的法教">
            <TeachingsColumn teachings={teachings} />
          </SectionCard>
          <SectionCard title="我的修行路径">
            <PathColumn pathNodes={pathNodes} />
          </SectionCard>
        </div>

        {/* Responsive override */}
        <style>{`
          @media (max-width: 768px) {
            .mj-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </div>
  );
}
